import React from "react";
import VideoCard from "../ui/VideoCard";
import ImageCard from "../ui/ImageCard";
import axios from "axios";
import {
  Snackbar,
  Alert,
  Grid,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import OcrForm from "../ui/OcrForm";
import FaceVerificationForm from "../ui/FaceVerificationForm";
import { cropImage, resizeImage } from "../../utils/image-util";
import {
  convertAddressOcrData,
  getOcrData,
  ocrBackCardChecking,
  ocrFrontCardChecking,
} from "../../utils/ocr";
import {
  faceVerificationChecking,
  getFaceVerificationData,
} from "../../utils/face-verification";
import { FaceMesh } from "@mediapipe/face_mesh";
import {
  BackCardDialogBody,
  FaceDialogBody,
  FrontCardDialogBody,
} from "../ui/tutorial-popup-body";
import { useStateRef } from "../../utils/custom-react-hook";

const SMALL_IMAGE_DATA =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBg+A8AAQQBAHAgZQsAAAAASUVORK5CYII=";

// Page for no card detection
export default function HomePage() {
  //---------- Face Detection ----------//
  const [faceDetection, setFaceDetection, faceDetectionRef] = useStateRef(null);
  //Get requestId
  const [requestId, setRequestId] = React.useState(null);
  // Dung useCallback de ham khong bi khoi tao lai khi component render
  const getRequestIdFromBackend = React.useCallback(async () => {
    try {
      const res = await axios.get(process.env.REACT_APP_API_REQUEST_URL);
      if (res.data.success) {
        setRequestId(res.data.request_id);
        console.log("Mã request:", res.data.request_id);
      } else {
        handleAlert("Không tạo được request_id");
      }
    } catch (e) {
      handleAlert("Lỗi kết nối backend: " + e.message);
    }
  }, []);

  // Get url_re
  const urlReFlag = new URL(window.location.href).searchParams.get("url_re");
  const handleBackNavigation = () => {
    // Get link .env dua tren flag (VD: REACT_APP_URL_RE_1)
    const envVarName = `REACT_APP_URL_RE_${urlReFlag}`;
    const dynamicUrl = process.env[envVarName];

    // Neu co link tuong ung trong .env thi lay, khong thi ce mac dinh
    if (dynamicUrl) {
      window.location.href = dynamicUrl;
    } else {
      window.location.href = process.env.REACT_APP_API_BACK_URL;
    }
  };

  //Get link chuyen tiep khi eKYC thanh cong
  const getNextNavigationUrl = () => { 
    const envNextName = `REACT_APP_API_NEXT_${urlReFlag}`;
    const dynamicUrlN = process.env[envNextName];
    return dynamicUrlN ? dynamicUrlN : process.env.REACT_APP_API_NEXT_URL;
  };
  /*eslint-disable */
  React.useEffect(() => {
    // Load model behind the scene
    const faceMesh = new FaceMesh({
      locateFile: (file) => {
        return "/face_mesh/" + file;
      },
    });
    faceMesh.setOptions({
      selfieMode: true,
      maxNumFaces: 1,
      refineLandmarks: true,
    });

    let smallImage = new Image();
    smallImage.src = SMALL_IMAGE_DATA;
    faceMesh.onResults(() => {
      setFaceDetection(faceMesh);
    });
    faceMesh.send({ image: smallImage });

    // Clean up when component unmount. This is react syntax.
    return () => {
      if (
        faceDetectionRef.current !== null &&
        faceDetectionRef.current !== undefined
      ) {
        faceDetectionRef.current.close();
      }
    };
  }, []);
  /*eslint-enable */

  //---------- Snackbar stuff ----------//
  // The only important thing that you need to know is that you can use handleAlert function to display error message
  // handleAlert can also be passed to other child components
  const [snackPack, setSnackPack] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [messageInfo, setMessageInfo] = React.useState(undefined);

  React.useEffect(() => {
    getRequestIdFromBackend();
    if (snackPack.length && !messageInfo) {
      // Set a new snack when we don't have an active one
      setMessageInfo({ ...snackPack[0] });
      setSnackPack((prev) => prev.slice(1));
      setOpen(true);
    } else if (snackPack.length && messageInfo && open) {
      // Close an active snack when a new one is added
      setOpen(false);
    }
  }, [snackPack, messageInfo, open, getRequestIdFromBackend]);

  const handleAlert = (message, warning) => {
    setSnackPack((prev) => [
      ...prev,
      { message, warning, key: new Date().getTime() },
    ]);
  };

  const handleAuthSuccess = () => {
    setShowSuccessPopup(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleExited = () => {
    setMessageInfo(undefined);
  };
  const [showSuccessPopup, setShowSuccessPopup] = React.useState(false);
  //---------- Main stuff ----------//

  // All images are url base64 data
  const [frontCardImage, setFrontCardImage] = React.useState(null);
  const [backCardImage, setBackCardImage] = React.useState(null);
  const [liveFaceImage, setLiveFaceImage] = React.useState(null);

  // Result data after calling OCR/FaceVerification API
  const [ocrFrontCardResult, setOcrFrontCardResult] = React.useState({});
  const [ocrBackCardResult, setOcrBackCardResult] = React.useState({});
  const [faceIdResult, setFaceIdResult] = React.useState({});

  // Loading state of the app when waiting for response from API
  const [loading, setLoading] = React.useState(false);

  // Feature enable
  const [backCardDisable, setBackCardDisable] = React.useState(true);
  const [liveFaceDisable, setLiveFaceDisable] = React.useState(true);
  const [submitDisable, setSubmitDisable] = React.useState(true);

  //Result insert member
  const [ekycSuccess, setEkycSuccess] = React.useState(false);
  const [showExistedModal, setShowExistedModal] = React.useState(false);



  const updateFrontCardImage = async (
    data,
    width,
    height,
    cropWidth,
    cropHeight
  ) => {
    setLoading(true);
    let resizedImageData = await cropImage(
      data,
      width,
      height,
      cropWidth,
      cropHeight
    );
    setFrontCardImage(resizedImageData);
    setOcrFrontCardResult({});

    // Resetting data since back card and live face image are based on front card result
    backCardImage && setBackCardImage(null);
    liveFaceImage && setLiveFaceImage(null);
    setOcrBackCardResult({});
    setFaceIdResult({});
    setBackCardDisable(true);
    setLiveFaceDisable(true);
    setSubmitDisable(true);
    try {
      console.log(requestId);
      let ocrData = await getOcrData(resizedImageData, requestId);
      await convertAddressOcrData(ocrData);
      Object.keys(ocrData).forEach((key) => {
        if (
          ocrData[key] === null ||
          ocrData[key] === "N/A" ||
          ocrData[key] === ""
        ) {
          delete ocrData[key];
        }
      });
      console.log("-----Front card-----");
      console.log(ocrData);
      setOcrFrontCardResult(ocrData);
      let frontCardValidation = await ocrFrontCardChecking(ocrData);
      if (!frontCardValidation.success) {
        handleAlert(frontCardValidation.message);
      } else {
        setBackCardDisable(false);
      }
    } catch (e) {
      console.log(e);
      handleAlert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateBackCardImage = async (
    data,
    width,
    height,
    cropWidth,
    cropHeight
  ) => {
    setLoading(true);
    let resizedImageData = await cropImage(
      data,
      width,
      height,
      cropWidth,
      cropHeight
    );
    setBackCardImage(resizedImageData);
    setOcrBackCardResult({});

    liveFaceImage && setLiveFaceImage(null);
    setFaceIdResult({});
    setLiveFaceDisable(true);
    setSubmitDisable(true);

    try {
      let ocrData = await getOcrData(resizedImageData, requestId);
      Object.keys(ocrData).forEach((key) => {
        if (
          ocrData[key] === null ||
          ocrData[key] === "N/A" ||
          ocrData[key] === ""
        ) {
          delete ocrData[key];
        }
      });
      console.log("-----Back card-----");
      console.log(ocrData);
      setOcrBackCardResult(ocrData);

      let backCardValidation = await ocrBackCardChecking(
        ocrData,
        ocrFrontCardResult
      );
      if (!backCardValidation.success) {
        handleAlert(backCardValidation.message);
      } else {
        setLiveFaceDisable(false);
      }
    } catch (e) {
      console.log(e);
      handleAlert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateLiveFaceImage = async (data) => {
    setFaceIdResult({});

    setLiveFaceImage(data);
    setLoading(true);
    setSubmitDisable(true);

    try {
      let resizedFaceImage = await resizeImage(data);
      let resizedFrontCardImage = await resizeImage(frontCardImage);
      let faceVerificationData = await getFaceVerificationData(
        resizedFrontCardImage,
        resizedFaceImage,
        requestId
      );
      Object.keys(faceVerificationData).forEach((key) => {
        if (
          faceVerificationData[key] === null ||
          faceVerificationData[key] === "N/A" ||
          faceVerificationData[key] === ""
        ) {
          delete faceVerificationData[key];
        }
      });
      console.log("-----Face Verification-----");
      console.log(faceVerificationData);
      setFaceIdResult(faceVerificationData);

      let faceValidation = await faceVerificationChecking(faceVerificationData);
      if (!faceValidation.success) {
        handleAlert(faceValidation.message);
      } else {
        if (
          faceVerificationData.verify_result === 2 ||
          faceVerificationData.verify_result === 1
        ) {
          setSubmitDisable(false);
          // Lấy domain từ URL và gửi kèm
          const currentDomain = new URL(window.location.href).searchParams.get("domain") || "default";
          // Khuôn mặt khớp => gọi upload.php để lưu file
          const formData = new FormData();

          formData.append("front_card", frontCardImage);
          formData.append("back_card", backCardImage);
          formData.append("face", data); // data chính là liveFaceImage
          formData.append("domain_name", currentDomain);
          formData.append("request_id", requestId);
          // Gửi cả thông tin OCR & kết quả eKYC (chuỗi JSON)
          formData.append("ocr_front", JSON.stringify(ocrFrontCardResult));
          formData.append("ocr_back", JSON.stringify(ocrBackCardResult));
          formData.append("face_result", JSON.stringify(faceVerificationData));

          try {
            const response = await fetch(process.env.REACT_APP_API_UPLOAD_URL, {
              method: "POST",
              body: formData,
            });

            const result = await response.json();
            console.log("Upload result:", result);
            if (result.status === "existed") {
              setLoading(false);
              setShowExistedModal(true); // Hiển thị Modal tự tạo
              return; // Dừng xử lý
            }
            if (result.success) {
              //Đánh dấu eKYC thành công
              setEkycSuccess(true);
              //Disable Upload / Camera

              //Lấy ID từ kết quả trả về
              const newId = result.member_id;
              const token = result.token; // Lấy token từ kết quả trả về
              //Hiển thị snackbar
              handleAuthSuccess();

              //Sau 2–3 giây, chuyển sang bước tiếp theo
              setTimeout(() => {
                const baseUrl = getNextNavigationUrl();
                // Kiểm tra xem baseUrl đã có dấu ? chưa để nối tham số cho đúng
                const separator = baseUrl.includes('?') ? '&' : '?';
                window.location.href = `${baseUrl}${separator}id=${newId}&token=${token}`;
              }, 3000);
            } else {
              handleAlert("Lưu dữ liệu thất bại: " + (result.error || ""));
            }
          } catch (e) {
            console.error("Error calling upload.php:", e);
            handleAlert("Không thể gửi ảnh về server: " + e.message);
          }

        }
        if (faceValidation.message !== "OK.") {
          handleAlert(faceValidation.message, true);
        }
      }
    } catch (e) {
      console.log(e);
      handleAlert(e.message);
    } finally {
      setLoading(false);
    }
  };
  if (process.env.REACT_APP_UPLOAD_ANH_GIAY_TO !== "1") {
    return (
      <>
        <Grid container spacing={2} sx={{ pt: 2 }}>
          <Grid item xs={12} lg={6}>
            <VideoCard
              title="1. Ảnh CMT mặt trước"
              popUpTitle="Để CMT mặt trước vào gần máy ảnh"
              imageData={frontCardImage}
              setImageData={updateFrontCardImage}
              handleAlert={handleAlert}
              tutorialTitle="Hướng dẫn chụp mặt trước CMT"
              tutorialContent={FrontCardDialogBody}
            />
            <VideoCard
              title="2. Ảnh CMT mặt sau"
              popUpTitle="Để CMT mặt sau vào gần máy ảnh"
              imageData={backCardImage}
              setImageData={updateBackCardImage}
              handleAlert={handleAlert}
              disabled={backCardDisable}
              tutorialTitle="Hướng dẫn chụp mặt sau CMT"
              tutorialContent={BackCardDialogBody}
            />
            <VideoCard
              title="3. Ảnh khuôn mặt"
              popUpTitle="Hãy làm theo chỉ dẫn phía dưới"
              imageData={liveFaceImage}
              setImageData={updateLiveFaceImage}
              handleAlert={handleAlert}
              disabled={liveFaceDisable || faceDetection == null}
              faceDetection={faceDetection}
              tutorialTitle="Hướng dẫn xác thực khuôn mặt"
              tutorialContent={FaceDialogBody}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <OcrForm
              frontCardData={ocrFrontCardResult}
              backCardData={ocrBackCardResult}
            />
            {/* <FaceVerificationForm faceData={faceIdResult} /> */}
          </Grid>
        </Grid>

        <Snackbar
          key={messageInfo ? messageInfo.key : undefined}
          open={open}
          autoHideDuration={6000}
          onClose={handleClose}
          TransitionProps={{ onExited: handleExited }}
        >
          <Alert
            onClose={handleClose}
            severity={
              messageInfo ? (messageInfo.warning ? "warning" : "error") : "error"
            }
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {messageInfo ? messageInfo.message : undefined}
          </Alert>
        </Snackbar>

        <Backdrop open={loading}>
          <CircularProgress color="info"></CircularProgress>
        </Backdrop>
        {showExistedModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 9999
          }}>
            <div style={{
              background: 'white', padding: '40px', borderRadius: '15px',
              textAlign: 'center', maxWidth: '450px', width: '90%',
              boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>⚠️</div>
              <h2 style={{ color: '#d32f2f', marginBottom: '15px', fontWeight: 'bold' }}>
                Tên miền đã tồn tại!
              </h2>
              <p style={{ color: '#333', fontSize: '16px', lineHeight: '1.6' }}>
                Tên miền này đã được đăng ký trong hệ thống. <br />
                Vui lòng chọn một tên miền khác để tiếp tục.
              </p>
              <button
                onClick={handleBackNavigation}
                style={{
                  marginTop: '30px', padding: '12px 35px', backgroundColor: '#1976d2',
                  color: 'white', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontWeight: 'bold', fontSize: '16px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
              >
                Quay về trang chọn tên miền
              </button>
            </div>
          </div>
        )}

        
        {showSuccessPopup && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 10000
          }}>
            <div style={{
              background: 'white', padding: '35px', borderRadius: '20px',
              textAlign: 'center', maxWidth: '420px', width: '90%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
              animation: 'popIn 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55)'
            }}>
              <div style={{
                width: '70px', height: '70px', backgroundColor: '#4CAF50',
                borderRadius: '50%', display: 'flex', justifyContent: 'center',
                alignItems: 'center', margin: '0 auto 20px'
              }}>
                <span style={{ color: 'white', fontSize: '40px', fontWeight: 'bold' }}>✓</span>
              </div>
              <h2 style={{ color: '#1b5e20', margin: '0 0 10px' }}>Xác thực thành công!</h2>
              <p style={{ color: '#555', marginBottom: '25px' }}>
                Đang chuyển hướng trong giây lát...
              </p>
              <div style={{ width: '100%', height: '6px', backgroundColor: '#eee', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: '100%', height: '100%', backgroundColor: '#4CAF50', animation: 'progressBar 3s linear forwards' }}></div>
              </div>
            </div>
            <style>{`
              @keyframes popIn { 0% { transform: scale(0.7); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
              @keyframes progressBar { 0% { width: 0%; } 100% { width: 100%; } }
            `}</style>
          </div>
        )}
      </>
    );
  }
  else {
    if (process.env.REACT_APP_UPLOAD_ANH_KHUON_MAT !== "1") {
      return (
        <>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12} lg={6}>
              <ImageCard
                title="1. Ảnh CMT mặt trước"
                imageData={frontCardImage}
                setImageData={updateFrontCardImage}
              />
              <ImageCard
                title="2. Ảnh CMT mặt sau"
                imageData={backCardImage}
                setImageData={updateBackCardImage}
                disabled={backCardDisable}
              />
              <VideoCard
                title="3. Ảnh khuôn mặt"
                popUpTitle="Hãy làm theo chỉ dẫn phía dưới"
                imageData={liveFaceImage}
                setImageData={updateLiveFaceImage}
                handleAlert={handleAlert}
                disabled={liveFaceDisable || faceDetection == null}
                faceDetection={faceDetection}
                tutorialTitle="Hướng dẫn xác thực khuôn mặt"
                tutorialContent={FaceDialogBody}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <OcrForm
                frontCardData={ocrFrontCardResult}
                backCardData={ocrBackCardResult}
              />
              {/* <FaceVerificationForm faceData={faceIdResult} /> */}
            </Grid>
          </Grid>

          <Snackbar
            key={messageInfo ? messageInfo.key : undefined}
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            TransitionProps={{ onExited: handleExited }}
          >
            <Alert
              onClose={handleClose}
              severity={
                messageInfo ? (messageInfo.warning ? "warning" : "error") : "error"
              }
              sx={{ width: "100%" }}
              elevation={6}
              variant="filled"
            >
              {messageInfo ? messageInfo.message : undefined}
            </Alert>
          </Snackbar>

          <Backdrop open={loading}>
            <CircularProgress color="info"></CircularProgress>
          </Backdrop>
          {showExistedModal && (
            <div style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 9999
            }}>
              <div style={{
                background: 'white', padding: '40px', borderRadius: '15px',
                textAlign: 'center', maxWidth: '450px', width: '90%',
                boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                animation: 'fadeIn 0.3s ease-in-out'
              }}>
                <div style={{ fontSize: '60px', marginBottom: '20px' }}>⚠️</div>
                <h2 style={{ color: '#d32f2f', marginBottom: '15px', fontWeight: 'bold' }}>
                  Tên miền đã tồn tại!
                </h2>
                <p style={{ color: '#333', fontSize: '16px', lineHeight: '1.6' }}>
                  Tên miền này đã được đăng ký trong hệ thống. <br />
                  Vui lòng chọn một tên miền khác để tiếp tục.
                </p>
                <button
                  onClick={handleBackNavigation}
                  style={{
                    marginTop: '30px', padding: '12px 35px', backgroundColor: '#1976d2',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: 'bold', fontSize: '16px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  Quay về trang chọn tên miền
                </button>
              </div>
            </div>
          )}

          {showSuccessPopup && (
            <div style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 10000
            }}>
              <div style={{
                background: 'white', padding: '35px', borderRadius: '20px',
                textAlign: 'center', maxWidth: '420px', width: '90%',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                animation: 'popIn 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55)'
              }}>
                <div style={{
                  width: '70px', height: '70px', backgroundColor: '#4CAF50',
                  borderRadius: '50%', display: 'flex', justifyContent: 'center',
                  alignItems: 'center', margin: '0 auto 20px'
                }}>
                  <span style={{ color: 'white', fontSize: '40px', fontWeight: 'bold' }}>✓</span>
                </div>
                <h2 style={{ color: '#1b5e20', margin: '0 0 10px' }}>Xác thực thành công!</h2>
                <p style={{ color: '#555', marginBottom: '25px' }}>
                  Đang chuyển hướng trong giây lát...
                </p>
                <div style={{ width: '100%', height: '6px', backgroundColor: '#eee', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: '100%', height: '100%', backgroundColor: '#4CAF50', animation: 'progressBar 3s linear forwards' }}></div>
                </div>
              </div>
              <style>{`
                @keyframes popIn { 0% { transform: scale(0.7); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
                @keyframes progressBar { 0% { width: 0%; } 100% { width: 100%; } }
              `}</style>
            </div>
          )}
        </>
      );
    }
    else {
      return (
        <>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12} lg={6}>
              <ImageCard
                title="1. Ảnh CMT mặt trước"
                imageData={frontCardImage}
                setImageData={updateFrontCardImage}
                disabled={ekycSuccess}
              />
              <ImageCard
                title="2. Ảnh CMT mặt sau"
                imageData={backCardImage}
                setImageData={updateBackCardImage}
                disabled={backCardDisable || ekycSuccess}
              />
              <ImageCard
                title="3. Ảnh khuôn mặt"
                imageData={liveFaceImage}
                setImageData={updateLiveFaceImage}
                disabled={liveFaceDisable || ekycSuccess}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <OcrForm
                frontCardData={ocrFrontCardResult}
                backCardData={ocrBackCardResult}
              />
              {/* <FaceVerificationForm faceData={faceIdResult} /> */}
            </Grid>
          </Grid>

          <Snackbar
            key={messageInfo ? messageInfo.key : undefined}
            open={open}
            autoHideDuration={6000}
            onClose={handleClose}
            TransitionProps={{ onExited: handleExited }}
          >
            <Alert
              onClose={handleClose}
              severity={
                messageInfo ? (messageInfo.warning ? "warning" : "error") : "error"
              }
              sx={{ width: "100%" }}
              elevation={6}
              variant="filled"
            >
              {messageInfo ? messageInfo.message : undefined}
            </Alert>
          </Snackbar>

          <Backdrop open={loading}>
            <CircularProgress color="info"></CircularProgress>
          </Backdrop>
          {showExistedModal && (
            <div style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 9999
            }}>
              <div style={{
                background: 'white', padding: '40px', borderRadius: '15px',
                textAlign: 'center', maxWidth: '450px', width: '90%',
                boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                animation: 'fadeIn 0.3s ease-in-out'
              }}>
                <div style={{ fontSize: '60px', marginBottom: '20px' }}>⚠️</div>
                <h2 style={{ color: '#d32f2f', marginBottom: '15px', fontWeight: 'bold' }}>
                  Tên miền đã tồn tại!
                </h2>
                <p style={{ color: '#333', fontSize: '16px', lineHeight: '1.6' }}>
                  Tên miền này đã được đăng ký trong hệ thống. <br />
                  Vui lòng chọn một tên miền khác để tiếp tục.
                </p>
                <button
                  onClick={handleBackNavigation}
                  style={{
                    marginTop: '30px', padding: '12px 35px', backgroundColor: '#1976d2',
                    color: 'white', border: 'none', borderRadius: '8px',
                    cursor: 'pointer', fontWeight: 'bold', fontSize: '16px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  Quay về trang chọn tên miền
                </button>
              </div>
            </div>
          )}

          {showSuccessPopup && (
            <div style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex',
              justifyContent: 'center', alignItems: 'center', zIndex: 10000
            }}>
              <div style={{
                background: 'white', padding: '35px', borderRadius: '20px',
                textAlign: 'center', maxWidth: '420px', width: '90%',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                animation: 'popIn 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55)'
              }}>
                <div style={{
                  width: '70px', height: '70px', backgroundColor: '#4CAF50',
                  borderRadius: '50%', display: 'flex', justifyContent: 'center',
                  alignItems: 'center', margin: '0 auto 20px'
                }}>
                  <span style={{ color: 'white', fontSize: '40px', fontWeight: 'bold' }}>✓</span>
                </div>
                <h2 style={{ color: '#1b5e20', margin: '0 0 10px' }}>Xác thực thành công!</h2>
                <p style={{ color: '#555', marginBottom: '25px' }}>
                  Đang chuyển hướng trong giây lát...
                </p>
                <div style={{ width: '100%', height: '6px', backgroundColor: '#eee', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: '100%', height: '100%', backgroundColor: '#4CAF50', animation: 'progressBar 3s linear forwards' }}></div>
                </div>
              </div>
              <style>{`
                @keyframes popIn { 0% { transform: scale(0.7); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
                @keyframes progressBar { 0% { width: 0%; } 100% { width: 100%; } }
              `}</style>
            </div>
          )}
        </>
      );
    }
  }
}
